import { ERROR_MESSAGES } from '../../lib/constants'
import {
  trackResumeImportCompleted,
  trackResumeImportStarted,
} from '../../lib/posthogEvents'
import { supabase } from '../../lib/supabase'

const RESUMES_BUCKET = 'resumes'

// Handle file change and validation
export async function handleFileChange(
  file: File,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  handleInputChangeWrapper: (field: 'resumeFile', value: File | null) => void,
  uploadResume: (file: File) => Promise<void>
) {
  // Validate file type
  const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  if (!allowedTypes.includes(file.type)) {
    setError('Please upload a PDF or Word document')
    return
  }
  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    setError('File size must be less than 10MB')
    return
  }
  
  handleInputChangeWrapper('resumeFile', file)
  setError(null)
  setResumeUploaded(false)
  
  // Upload resume immediately
  await uploadResume(file)
}

// Upload resume file
export async function uploadResume(
  file: File,
  setResumeUploading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  setResumeMdPath: React.Dispatch<React.SetStateAction<string | null>>,
  convertResumeToMarkdown: (file: File, originalFilePath: string) => Promise<void>
) {
  setResumeUploading(true)
  setError(null)
  trackResumeImportStarted('onboarding')

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setError('You must be logged in to upload a resume')
      setResumeUploading(false)
      return
    }

    const fileExt = file.name.split('.').pop()
    const fileName = `${user.id}/${Date.now()}.${fileExt}`
    
    // Try to upload directly - if bucket doesn't exist or RLS policies are wrong,
    // we'll get a specific error that we can handle
    const { error: uploadError } = await supabase.storage
      .from(RESUMES_BUCKET)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (uploadError) {
      // Log full error for debugging
      console.error('Resume upload error:', uploadError)
      console.error('Error details:', {
        message: uploadError.message,
        statusCode: 'statusCode' in uploadError ? (uploadError as { statusCode?: number }).statusCode : undefined,
        code: 'code' in uploadError ? (uploadError as { code?: string }).code : undefined,
        error: uploadError,
      })
      
      // Provide more helpful error messages based on error type
      const errorMessage = uploadError.message || String(uploadError)
      const errorCode = ('statusCode' in uploadError ? (uploadError as { statusCode?: number }).statusCode : undefined) || ('code' in uploadError ? (uploadError as { code?: string }).code : undefined)
      
      // Handle 400 Bad Request - usually means bucket doesn't exist or wrong configuration
      if (errorCode === 400 || errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
        throw new Error(
          'Ошибка 400: Bucket "resumes" не найден или неправильно настроен.\n\n' +
          'Пожалуйста, проверьте:\n' +
          '1. Bucket "resumes" создан в Supabase Dashboard → Storage\n' +
          '2. Название bucket точно "resumes" (без кавычек, в нижнем регистре)\n' +
          '3. Применена миграция для RLS политик:\n' +
          '   cd packages/web && npm run db:migrate\n' +
          '   Или через SQL Editor: supabase/migrations/20260117000002_setup_resumes_storage.sql\n' +
          '4. Убедитесь, что вы авторизованы (залогинены)\n\n' +
          `Подробнее: см. instructions/ONBOARDING_SETUP.md`
        )
      }
      
      // Handle bucket not found
      if (errorMessage.includes('Bucket not found') || 
          errorMessage.includes('does not exist') ||
          errorMessage.includes('not found')) {
        throw new Error(
          'Bucket "resumes" не найден в Supabase Storage.\n\n' +
          'Пожалуйста, создайте bucket:\n' +
          '1. Supabase Dashboard → Storage → New bucket\n' +
          '2. Название: "resumes" (точно, без кавычек)\n' +
          '3. Настройки: Приватный, лимит 10MB\n' +
          '4. После создания примените миграцию: cd packages/web && npm run db:migrate\n\n' +
          `Подробнее: см. instructions/ONBOARDING_SETUP.md`
        )
      }
      
      // Handle RLS errors
      if (errorMessage.includes('new row violates row-level security') || 
          errorMessage.includes('row-level security') ||
          errorMessage.includes('RLS') ||
          errorCode === 42501) {
        throw new Error(
          'Ошибка прав доступа (RLS).\n\n' +
          'Пожалуйста, выполните следующие шаги:\n' +
          '1. Убедитесь, что bucket "resumes" создан в Supabase Dashboard\n' +
          '2. Примените миграцию для RLS политик:\n' +
          '   cd packages/web && npm run db:migrate\n' +
          '   Или вручную через SQL Editor: supabase/migrations/20260117000002_setup_resumes_storage.sql\n' +
          '3. Убедитесь, что вы авторизованы (залогинены)\n\n' +
          `Подробнее: см. instructions/ONBOARDING_SETUP.md`
        )
      }
      
      // Generic error
      throw new Error(`Не удалось загрузить резюме: ${errorMessage}`)
    }

    setResumeFilePath(fileName)
    
    // Convert resume to Markdown after successful upload
    // Keep resumeUploading true during conversion
    await convertResumeToMarkdown(file, fileName)
    
    setResumeUploaded(true)
    trackResumeImportCompleted('onboarding', 'success')
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    setError(errorMessage || 'Failed to upload resume')
    setResumeFilePath(null)
    setResumeUploaded(false)
    setResumeMdPath(null)
    trackResumeImportCompleted('onboarding', 'error')
  } finally {
    // Reset uploading state after conversion is complete
    // (convertResumeToMarkdown handles resumeConverting state)
    setResumeUploading(false)
  }
}

// Upload AI memory file
export async function uploadAiMemory(
  file: File,
  setAiMemoryUploading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setAiMemoryFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setAiMemoryUploaded: React.Dispatch<React.SetStateAction<boolean>>
) {
  setAiMemoryUploading(true)
  setError(null)

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setError('Вы должны быть авторизованы для загрузки файла')
      setAiMemoryUploading(false)
      return
    }

    const fileExt = file.name.split('.').pop()
    const fileName = `${user.id}/ai-memory-${Date.now()}.${fileExt}`
    
    const { error: uploadError } = await supabase.storage
      .from(RESUMES_BUCKET) // Используем тот же bucket для простоты
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (uploadError) {
      console.error('AI memory upload error:', uploadError)
      throw new Error(`Не удалось загрузить файл памяти: ${uploadError.message}`)
    }

    setAiMemoryFilePath(fileName)
    setAiMemoryUploaded(true)
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    setError(errorMessage || 'Не удалось загрузить файл памяти')
    setAiMemoryFilePath(null)
    setAiMemoryUploaded(false)
  } finally {
    setAiMemoryUploading(false)
  }
}

// Convert resume to Markdown
export async function convertResumeToMarkdown(
  file: File,
  originalFilePath: string,
  setResumeConverting: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeMdPath: React.Dispatch<React.SetStateAction<string | null>>
) {
  setResumeConverting(true)
  setError(null)

  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error(ERROR_MESSAGES.MUST_BE_LOGGED_IN_TO_CONVERT_RESUME)
    }

    console.log('[Onboarding] Starting Markdown conversion for file:', file.name, 'path:', originalFilePath)

    // Create FormData to send file to API
    const formData = new FormData()
    formData.append('file', file)

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    console.log('[Onboarding] Calling API:', `${apiUrl}/api/resume/convert-to-markdown`)
    
    const response = await fetch(`${apiUrl}/api/resume/convert-to-markdown`, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to convert resume' }))
      const errorMessage = errorData.detail || 'Failed to convert resume to Markdown'
      console.error('[Onboarding] API conversion error:', response.status, errorMessage)
      throw new Error(errorMessage)
    }

    const result = await response.json()
    const markdownContent = result.markdown

    if (!markdownContent) {
      console.error('[Onboarding] No markdown content in API response:', result)
      throw new Error('No markdown content returned from conversion')
    }

    console.log('[Onboarding] Markdown conversion successful, length:', markdownContent.length, 'chars')

    // Save markdown to Supabase Storage
    const mdFileName = originalFilePath.replace(/\.[^.]+$/, '.md')
    console.log('[Onboarding] Saving Markdown to Storage:', mdFileName)
    
    // Create blob with text/markdown MIME type
    const mdBlob = new Blob([markdownContent], { type: 'text/markdown' })

    const { error: mdUploadError } = await supabase.storage
      .from(RESUMES_BUCKET)
      .upload(mdFileName, mdBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'text/markdown', // Use text/markdown MIME type for Markdown files
      })

    if (mdUploadError) {
      console.error('[Onboarding] Error uploading markdown to Storage:', mdUploadError)
      console.error('[Onboarding] Upload error details:', {
        message: mdUploadError.message,
        statusCode: 'statusCode' in mdUploadError ? (mdUploadError as { statusCode?: number }).statusCode : undefined,
        code: 'code' in mdUploadError ? (mdUploadError as { code?: string }).code : undefined,
      })
      // Don't fail the whole process if MD upload fails
      // The original file is already uploaded
    } else {
      setResumeMdPath(mdFileName)
      console.log('[Onboarding] Resume converted to Markdown successfully:', mdFileName)
    }
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    const errorStack = err instanceof Error ? err.stack : undefined
    console.error('[Onboarding] Error converting resume to Markdown:', errorMessage)
    console.error('[Onboarding] Error stack:', errorStack)
    // Show error to user but don't fail the whole upload process
    // The original file is already uploaded
    const errObj = err as { message?: string }
    setError(`Failed to convert resume to Markdown: ${errObj.message || 'Unknown error'}. The resume file was uploaded successfully.`)
  } finally {
    setResumeConverting(false)
  }
}
